import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@wms/shared';
import { useTranslation } from 'react-i18next';
import { ApiError, apiFetch } from '../../lib/api';
import { useCan } from '../../lib/session';
import type { MovementDetail, MovementListItem, ReverseMovementResponse } from './types';
import { SkeletonBlock, SkeletonRows } from '../skeleton';

const ROW_HEIGHT = 48;
const PAGE_SIZE = 100;

/** 冲销范围（M6 用户批准，movement.service.ts 同一断言的前端镜像）：仅 OUTBOUND/TRANSFER，
 * 未被冲销过，本身不是一笔冲销单。这里只是 UX 降噪——真正的强制永远在后端。 */
const REVERSIBLE_TYPES = new Set(['OUTBOUND', 'TRANSFER']);

export function canOfferReversal(item: MovementListItem): boolean {
  return (
    REVERSIBLE_TYPES.has(item.type) &&
    item.status !== 'REVERSED' &&
    item.reversalOfMovementId === null
  );
}

/** 流水页（S10）：出库/转移/入库/报损/过期处理等全部单据类型的统一时间线 + 冲销入口。 */
export function MovementsPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const canReverse = useCan(PERMISSIONS.INVENTORY_ADJUST);
  const [typeFilter, setTypeFilter] = useState('');
  const [confirmReverse, setConfirmReverse] = useState<MovementListItem | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });

  const { data, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['movements', typeFilter],
    queryFn: ({ pageParam }) =>
      apiFetch<{ items: MovementListItem[]; nextCursor: string | null }>(
        `/inventory/movements?limit=${PAGE_SIZE}${typeFilter ? `&type=${typeFilter}` : ''}${
          pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''
        }`,
      ),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = useMemo(() => (data?.pages ?? []).flatMap((p) => p.items), [data]);

  const parentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    const onScroll = (): void => {
      const lastIndex = Math.floor((el.scrollTop + el.clientHeight) / ROW_HEIGHT);
      if (lastIndex >= items.length - 10 && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [items.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const reverseMutation = useMutation({
    mutationFn: (movementId: string) =>
      apiFetch<ReverseMovementResponse>(`/inventory/movements/${movementId}/reverse`, {
        method: 'POST',
        headers: { 'idempotency-key': crypto.randomUUID() },
        body: {},
      }),
    onSuccess: () => {
      setConfirmReverse(null);
      void queryClient.invalidateQueries({ queryKey: ['movements'] });
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  return (
    <div>
      <div className="row row--between">
        <h1 style={{ fontSize: 'var(--text-2xl)' }}>{t('nav.movements')}</h1>
      </div>

      <div className="row" style={{ gap: 'var(--space-2)', margin: 'var(--space-3) 0' }}>
        {['', 'INBOUND', 'OUTBOUND', 'TRANSFER', 'DAMAGED', 'EXPIRED', 'ADJUSTMENT'].map((type) => (
          <button
            key={type || 'all'}
            type="button"
            className={typeFilter === type ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
            onClick={() => setTypeFilter(type)}
          >
            {type ? t(`movements.type${type}`) : t('movements.typeAll')}
          </button>
        ))}
      </div>

      {isPending ? (
        <SkeletonRows />
      ) : items.length === 0 ? (
        <div className="empty-state">
          <p>{t('movements.empty')}</p>
        </div>
      ) : (
        <div className="table" style={{ display: 'block' }}>
          <div
            className="row"
            style={{
              fontWeight: 600,
              padding: 'var(--space-2) 0',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <span style={{ width: '140px' }}>{t('movements.colDoc')}</span>
            <span style={{ width: '100px' }}>{t('movements.colType')}</span>
            <span style={{ flex: 1.5 }}>{t('movements.colPerformedAt')}</span>
            <span style={{ width: '120px' }}>{t('movements.colOperator')}</span>
            <span style={{ width: '120px' }}>{t('movements.colAuthorizer')}</span>
            <span style={{ width: '140px' }}>{t('movements.colStatus')}</span>
            <span style={{ width: '110px' }} />
          </div>
          <div ref={parentRef} style={{ maxHeight: '640px', overflow: 'auto' }}>
            {items.map((item) => (
              <div
                key={item.id}
                className="tree__row"
                style={{ height: `${ROW_HEIGHT}px`, cursor: 'pointer' }}
                onClick={() => setSelectedId(item.id)}
              >
                <span style={{ width: '140px' }}>{item.docNo}</span>
                <span style={{ width: '100px' }}>
                  {t(`movements.type${item.type}`, { defaultValue: item.type })}
                </span>
                <span style={{ flex: 1.5 }}>{fmt.format(new Date(item.performedAt))}</span>
                <span style={{ width: '120px' }}>{item.performedByName ?? '—'}</span>
                <span style={{ width: '120px' }}>{item.authorizerName ?? '—'}</span>
                <span style={{ width: '140px' }}>
                  {item.status === 'REVERSED' && (
                    <span className="badge badge--danger">{t('movements.statusReversed')}</span>
                  )}
                  {item.negativeOverride && (
                    <span className="badge badge--info">
                      {t('movements.negativeOverrideBadge')}
                    </span>
                  )}
                  {item.reversalOfMovementId && (
                    <span className="badge badge--info">{t('movements.isReversalBadge')}</span>
                  )}
                </span>
                <span style={{ width: '110px' }} onClick={(e) => e.stopPropagation()}>
                  {canReverse && canOfferReversal(item) && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => setConfirmReverse(item)}
                    >
                      {t('movements.reverse')}
                    </button>
                  )}
                </span>
              </div>
            ))}
            {isFetchingNextPage && <p>{t('app.loading')}</p>}
          </div>
        </div>
      )}

      {selectedId && (
        <MovementDetailModal movementId={selectedId} onClose={() => setSelectedId(null)} />
      )}

      {confirmReverse && (
        <div className="modal-overlay" role="presentation" onClick={() => setConfirmReverse(null)}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>{t('movements.reverseConfirmTitle')}</h2>
            <p>{t('movements.reverseConfirmBody', { doc: confirmReverse.docNo })}</p>
            {reverseMutation.error instanceof ApiError && (
              <p className="field-error">
                {t(`errors:${reverseMutation.error.code}`, {
                  defaultValue: reverseMutation.error.code,
                  ...reverseMutation.error.params,
                })}
              </p>
            )}
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn--ghost"
                disabled={reverseMutation.isPending}
                onClick={() => setConfirmReverse(null)}
              >
                {t('app.cancel')}
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={reverseMutation.isPending}
                onClick={() => reverseMutation.mutate(confirmReverse.id)}
              >
                {reverseMutation.isPending ? t('app.loading') : t('movements.reverseConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** 单据详情（用户反馈："点击一下会出现单据的页面，不然我要很麻烦才能去查那个单据是什么"） */
function MovementDetailModal({
  movementId,
  onClose,
}: {
  readonly movementId: string;
  readonly onClose: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });
  const { data: detail, isPending } = useQuery({
    queryKey: ['movements', 'detail', movementId],
    queryFn: () => apiFetch<MovementDetail>(`/inventory/movements/${movementId}`),
  });

  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '720px' }}
      >
        {isPending || !detail ? (
          <SkeletonBlock height={200} />
        ) : (
          <>
            <div className="row row--between">
              <h2>{detail.docNo}</h2>
              <span>
                {t(`movements.type${detail.type}`, { defaultValue: detail.type })}
                {detail.status === 'REVERSED' && (
                  <span className="badge badge--danger" style={{ marginLeft: 'var(--space-2)' }}>
                    {t('movements.statusReversed')}
                  </span>
                )}
              </span>
            </div>
            <p className="field-hint">
              {t('movements.detailPerformedAt', { at: fmt.format(new Date(detail.performedAt)) })}
              {' · '}
              {t('movements.detailOperator', { name: detail.performedByName ?? '—' })}
              {detail.authorizerName && (
                <>
                  {' · '}
                  {t('movements.detailAuthorizer', { name: detail.authorizerName })}
                </>
              )}
            </p>
            {detail.reasonText && <p>{t('movements.detailReason', { reason: detail.reasonText })}</p>}
            {detail.note && <p>{t('movements.detailNote', { note: detail.note })}</p>}
            {detail.reversalOfMovementId && (
              <p className="field-hint">{t('movements.detailIsReversalOf')}</p>
            )}
            {detail.reversedByMovementId && (
              <p className="field-hint">{t('movements.detailReversedBy')}</p>
            )}

            <div className="table-scroll" style={{ marginTop: 'var(--space-3)' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('movements.detailColProduct')}</th>
                    <th>{t('movements.detailColBatch')}</th>
                    <th>{t('movements.detailColFrom')}</th>
                    <th>{t('movements.detailColTo')}</th>
                    <th>{t('movements.detailColQty')}</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.lines.map((line) => (
                    <tr key={line.id}>
                      <td>{line.productName}</td>
                      <td>{line.batchNo}</td>
                      <td>{line.fromLocationLabel ?? '—'}</td>
                      <td>{line.toLocationLabel ?? '—'}</td>
                      <td>{line.qtyBase}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={onClose}>
                {t('app.close')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
